import { reactive } from 'vue'
import { apiFetch } from './api'
import type { SessionUser } from './session'
import { newId } from '@/utils/id'

export type SubmissionStatus = 'rascunho' | 'enviado'
export type SubmissionCategory = 'relatorio' | 'dialogo' | 'permissao' | 'ocorrido'
export type SubmissionReportLanguage = 'pt' | 'en' | 'es' | 'de' | 'fr' | 'it' | 'zh' | 'ja' | 'ko' | 'ru' | 'nl'

const VALID_LANGS = new Set<SubmissionReportLanguage>(['pt','en','es','de','fr','it','zh','ja','ko','ru','nl'])
function normalizeLang(v: unknown): SubmissionReportLanguage {
  const s = String(v || '').toLowerCase()
  // back-compat: old records stored 'PT'/'EN'
  if (s === 'pt' || s === 'PT') return 'pt'
  if (s === 'en' || s === 'EN') return 'en'
  return VALID_LANGS.has(s as SubmissionReportLanguage) ? (s as SubmissionReportLanguage) : 'pt'
}

export interface SubmissionActivity {
  id: string
  description: string
  images: string[]
}

export interface SubmissionSection {
  id: string
  text: string
  images: string[]
}

export interface SubmissionRequestedItem {
  id: string
  nome: string
  descricao: string
  link: string
  images: string[]
}

export interface Submission {
  id: string
  title: string
  category: SubmissionCategory
  attraction: string
  reportDate: string
  collaborators: string[]
  tools: SubmissionSection[]
  activities: SubmissionActivity[]
  observations: SubmissionSection[]
  requestedItems?: SubmissionRequestedItem[]
  reportLanguage: SubmissionReportLanguage
  status: SubmissionStatus
  author: SessionUser
  coAuthors: string[]
  creatorUsername?: string
  createdOnBehalfOfUsername?: string
  createdAt: string
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function normalizeCategory(value: unknown): SubmissionCategory {
  if (value === 'dialogo' || value === 'permissao' || value === 'ocorrido') return value
  return 'relatorio'
}

function normalizeSection(value: unknown): SubmissionSection {
  if (typeof value === 'string') {
    return { id: generateId(), text: value, images: [] }
  }
  const r = asRecord(value)
  return {
    id: String(r.id || generateId()),
    text: String(r.text || ''),
    images: Array.isArray(r.images) ? r.images.filter((v): v is string => typeof v === 'string') : []
  }
}

function normalizeRequestedItem(value: unknown): SubmissionRequestedItem {
  const r = asRecord(value)
  return {
    id: String(r.id || generateId()),
    nome: String(r.nome || ''),
    descricao: String(r.descricao || ''),
    link: String(r.link || ''),
    images: Array.isArray(r.images) ? r.images.filter((v): v is string => typeof v === 'string') : []
  }
}

function normalizeSubmission(item: unknown): Submission {
  const record = asRecord(item)
  const activitiesRaw = Array.isArray(record.activities) ? record.activities : []
  const activities: SubmissionActivity[] = activitiesRaw
    .map((activity) => asRecord(activity))
    .map((activity) => ({
      id: String(activity.id || newId()),
      description: String(activity.description || ''),
      images: Array.isArray(activity.images)
        ? activity.images.filter((value): value is string => typeof value === 'string')
        : []
    }))

  const author = asRecord(record.author)
  return {
    id: String(record.id || ''),
    title: String(record.title || ''),
    category: normalizeCategory(record.category),
    attraction: String(record.attraction || ''),
    reportDate: String(record.reportDate || new Date().toISOString().slice(0, 10)),
    collaborators: Array.isArray(record.collaborators)
      ? record.collaborators.filter((value): value is string => typeof value === 'string')
      : [],
    coAuthors: Array.isArray(record.coAuthors)
      ? record.coAuthors.filter((value): value is string => typeof value === 'string')
      : [],
    tools: Array.isArray(record.tools) ? record.tools.map(normalizeSection) : [],
    activities,
    observations: Array.isArray(record.observations) ? record.observations.map(normalizeSection) : [],
    requestedItems: Array.isArray(record.requestedItems) ? record.requestedItems.map(normalizeRequestedItem) : [],
    reportLanguage: normalizeLang(record.reportLanguage),
    status: record.status === 'enviado' ? 'enviado' : 'rascunho',
    author: {
      name: String(author.name || 'Desconhecido'),
      role:
        author.role === 'admin-master' || author.role === 'admin-obra' || author.role === 'user'
          ? author.role
          : 'user',
      username: String(author.username || ''),
      uid: String(author.uid || ''),
      avatar: String(author.avatar || ''),
      roleId: String(author.roleId || ''),
      subordinateUsernames: Array.isArray(author.subordinateUsernames)
        ? author.subordinateUsernames.filter((value): value is string => typeof value === 'string')
        : [],
      hasSubordinates: Boolean(author.hasSubordinates)
    },
    creatorUsername: String(record.creatorUsername || ''),
    createdOnBehalfOfUsername: String(record.createdOnBehalfOfUsername || ''),
    createdAt: String(record.createdAt || new Date().toISOString())
  }
}

const state = reactive({
  items: [] as Submission[],
  loading: false
})

function generateId() {
  return newId()
}

export const submissionsStore = {
  state,
  async refresh() {
    state.loading = true
    try {
      const items = await apiFetch<unknown[]>('/submissions')
      state.items = items.map(normalizeSubmission).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      return state.items
    } finally {
      state.loading = false
    }
  },
  async addSubmission(data: Omit<Submission, 'id' | 'createdAt'>) {
    const id = generateId()
    const payload = {
      id,
      ...data,
      createdAt: new Date().toISOString()
    }

    // If offline and offline mode is enabled, queue for later sync
    if (!navigator.onLine && localStorage.getItem('offlineMode') === '1') {
      const { addPendingReport } = await import('@/utils/offlineDB')
      const { refreshPendingCount } = await import('@/composables/useOfflineMode')
      await addPendingReport({ id, payload, createdAt: payload.createdAt })
      await refreshPendingCount()
      // Update local state so the UI shows it immediately
      state.items = [normalizeSubmission(payload), ...state.items]
      return id
    }

    await apiFetch<{ ok: boolean }>('/submissions', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    await this.refresh()
    return id
  },
  async updateSubmission(
    id: string,
    updates: Partial<Omit<Submission, 'id' | 'author' | 'createdAt'>>
  ) {
    const item = state.items.find((entry) => entry.id === id)
    if (!item) return
    const payload: Submission = {
      ...item,
      ...updates,
      id: item.id,
      author: item.author,
      createdAt: item.createdAt
    }
    await apiFetch<{ ok: boolean }>('/submissions', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    await this.refresh()
  },
  async deleteSubmission(id: string) {
    await apiFetch<{ ok: boolean }>(`/submissions/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    })
    await this.refresh()
  },
  getById(id: string) {
    return state.items.find((entry) => entry.id === id) || null
  },
  byAuthor(name: string) {
    return state.items.filter((entry) => entry.author.name === name)
  },
  latestSupervisorByAttraction(attraction: string) {
    const filtered = state.items
      .filter((entry) => entry.attraction === attraction && (entry.author.role === 'admin-master' || entry.author.role === 'admin-obra'))
      .sort((a, b) => (a.reportDate < b.reportDate ? 1 : -1))
    return filtered[0] || null
  },
  all() {
    return state.items
  }
}
